import * as Dialog from "@radix-ui/react-dialog";
import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";

enum ModalSize {
  MEDIUM = "medium",
  LARGE = "large",
  EXTRA_LARGE = "extra-large"
}

const SIZE_CLASS: Record<ModalSize, string> = {
  [ModalSize.MEDIUM]: "w-[400px] min-h-[130px]",
  [ModalSize.LARGE]: "w-[600px] min-h-[130px]",
  [ModalSize.EXTRA_LARGE]: "w-[800px]"
};

interface ModalProps {
  children: ReactNode;
  className?: string;
  hasCloseIcon?: boolean;
  hasOverlay?: boolean;
  hasTitleBorder?: boolean;
  isOpen?: boolean;
  onClose?: (open: boolean) => void;
  /** Radix `onEscapeKeyDown` passthrough — call `event.preventDefault()` to keep the modal open (e.g. while a nested picker is open). */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** Radix `onCloseAutoFocus` passthrough — call `event.preventDefault()` to put focus somewhere of your own once the modal has closed. */
  onCloseAutoFocus?: (event: Event) => void;
  size?: ModalSize;
  title?: ReactNode;
  titleClassName?: string;
  trigger?: ReactNode;
}

function isDialogSafe(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("[data-dialog-safe]");
}

const Modal = ({
  isOpen,
  onClose,
  onEscapeKeyDown,
  onCloseAutoFocus,
  hasOverlay = true,
  hasCloseIcon = true,
  hasTitleBorder = false,
  trigger,
  size = ModalSize.MEDIUM,
  children,
  title,
  titleClassName,
  className
}: ModalProps) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!isOpen) setIsScrolled(false);
  }, [isOpen]);

  const handleScrollCapture = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled((e.target as HTMLElement).scrollTop > 0);
  }, []);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        {hasOverlay && (
          <Dialog.Overlay className="fixed inset-0 z-500 bg-black/22 data-[state=closed]:animate-modal-overlay-out data-[state=open]:animate-modal-overlay-in" />
        )}
        <Dialog.Description className="sr-only">{title || "Modal"}</Dialog.Description>
        <Dialog.Content
          onEscapeKeyDown={onEscapeKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
          // Portaled helpers (e.g. the variable picker) mark themselves `data-dialog-safe`; a
          // pointer/focus landing in one is not "outside" this dialog.
          onPointerDownOutside={(event) => {
            if (isDialogSafe(event.target)) event.preventDefault();
          }}
          onFocusOutside={(event) => {
            if (isDialogSafe(event.target)) event.preventDefault();
          }}
          onScrollCapture={handleScrollCapture}
          className={filterClasses(
            "fixed top-1/2 left-1/2 z-500 -translate-x-1/2 -translate-y-1/2 rounded bg-white shadow-[0_6px_16px_0_rgba(0,0,0,0.12)] data-[state=closed]:animate-modal-content-out data-[state=open]:animate-modal-content-in",
            SIZE_CLASS[size],
            className
          )}
        >
          <Dialog.Title
            className={filterClasses(
              "heading-base-alt flex shrink-0 items-center justify-between p-4 transition-shadow duration-150",
              hasTitleBorder ? "border-b border-grey-200" : isScrolled ? "shadow-panel" : "",
              titleClassName
            )}
          >
            {title}
            {hasCloseIcon && (
              <Dialog.Close className="cursor-pointer rounded-full p-1">
                <IoClose className="size-5 text-black transition-colors duration-200 hover:text-grey-500" />
              </Dialog.Close>
            )}
          </Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { Modal, ModalSize };
